// WelcomeScreenWithAgents.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import MessageRenderer from './MessageRenderer';
import TypingIndicator from './TypingIndicator';

const agentCardVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 }
};

const startConversationVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 }
};

function AgentsAvailableScreen({
  onStartConversation,
  onContactNameChange,
  showStartConversation,
  availableAgents = [],
  showAvailableAgents,
  customization,
  selectedColor,
  textColor
}) {
  const displayedAgents = availableAgents.slice(0, 4);
  const formatAgentName = (agent) => {
    if (!agent.name) return '';

    const firstName = agent.name?.split(' ')[0];
    const upperCaseName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

    return upperCaseName;
  };

  // Render each agent's card
  const renderAgentCards = () =>
    displayedAgents.map((agent, index) => (
      <div className="flex flex-col items-center" key={agent.name + agent.avatar}>
        <motion.div
          key={agent.name}
          className="border-2 border-white rounded-xl w-12 h-12"
          variants={agentCardVariants}
          initial="hidden"
          animate="visible"
          transition={{ type: 'spring', delay: index * 0.1, duration: 0.5 }}
          style={{
            backgroundImage: `url(${agent.avatar})`,
            backgroundColor: '#d1d5db',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
          }}
        >
          {!agent.avatar && (
            <div className="flex h-full items-center justify-center">
              <p>{agent?.name?.slice(0, 1)?.toUpperCase()}</p>
            </div>
          )}
        </motion.div>
        <p className="truncate text-xs text-black text-center mt-1 w-14">
          {formatAgentName(agent)}
        </p>
      </div>
    ));
  const [zeroAgents, setZeroAgents] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const onSendMessageClick = () => {
    onStartConversation();

    if (zeroAgents) {
      setShowConfirmationMessage(true);
    }
  };

  useEffect(() => {
    setZeroAgents(availableAgents.length === 0);
  }, [availableAgents]);

  const mockMessages = [
    {
      senderType: 'agent',
      type: 'text',
      content: zeroAgents
        ? customization?.noAgentsScreen?.firstMessage
        : customization?.welcomeScreen?.firstMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      senderType: 'agent',
      type: 'text',
      content: (
        <div>
          <div>
            {zeroAgents
              ? customization?.noAgentsScreen?.inputMessage
              : customization?.welcomeScreen?.inputMessage}
          </div>
          <div className="flex flex-row justify-between border rounded-xl items-center p-2 mt-5 bg-white">
            <input
              className="h-full p-1 w-full focus:outline-none"
              type="text"
              placeholder={
                zeroAgents
                  ? customization?.noAgentsScreen?.textAreaPlaceholder
                  : customization?.welcomeScreen?.textAreaPlaceholder
              }
              onChange={(e) => onContactNameChange(e.target.value)}
            />
          </div>
        </div>
      ),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ];

  return (
    <div className="welcome-screen">
      <div
        className="chat-header relative p-8 bg-gradient-to-r pb-12"
        style={{ backgroundColor: selectedColor }}
      >
        <h1 className={`text-3xl font-bold ${textColor.token}`}>
          {zeroAgents
            ? customization?.noAgentsScreen?.title
            : customization?.welcomeScreen?.title}
        </h1>
        <p className={`text-sm mt-2 ${textColor.token}`}>
          {zeroAgents
            ? customization?.noAgentsScreen?.subtitle
            : customization?.welcomeScreen?.subtitle}
        </p>
      </div>
      {showAvailableAgents && availableAgents?.length > 0 && (
        <div className="flex relative -top-9 justify-center gap-2">
          {renderAgentCards()}
        </div>
      )}
      {/* Conversation Area */}
      <div className="conversation flex-grow px-6 pt-2 pb-5 overflow-hidden overflow-y-scroll scrollbar-hide">
        {mockMessages.map((message, index) => (
          <MessageRenderer
            key={index}
            message={message}
            selectedColor={selectedColor}
            textColor={textColor}
          />
        ))}
        <motion.div
          variants={startConversationVariants}
          initial="hidden"
          animate={
            showStartConversation && !showConfirmationMessage ? 'visible' : 'hidden'
          }
          transition={{ type: 'spring', delay: 0.5, duration: 0.5 }}
        >
          <button
            className={`${textColor.token} px-4 py-3 rounded-xl hover:opacity-80 shadow-lg font-bold text-sm btn btn-primary`}
            onClick={() => onSendMessageClick()}
            style={{ backgroundColor: selectedColor }}
          >
            {zeroAgents
              ? customization?.noAgentsScreen?.button
              : customization?.welcomeScreen?.button}
          </button>
        </motion.div>
      </div>
      {zeroAgents && showConfirmationMessage && (
        <motion.div
          variants={startConversationVariants}
          initial="hidden"
          animate={showConfirmationMessage ? 'visible' : 'hidden'}
          transition={{ type: 'spring', delay: 0.5, duration: 0.5 }}
        >
          <p className="flex text-center mx-8">
            {customization?.noAgentsScreen?.confirmationMessage}
          </p>
        </motion.div>
      )}
    </div>
  );
}

export default AgentsAvailableScreen;
